(() => {
  if (customElements.get('tb-article-reader')) return;
  class ArticleReader extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      const content = this.querySelector('[data-native-document-content]');
      if (!content) return;
      // Move native nodes, never replace article HTML or create invented copy.
      let section = null;
      for (const node of [...content.childNodes]) {
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'H2') {
          section = document.createElement('section');
          section.className = 'tb-article-section';
          content.insertBefore(section, node);
          section.append(node);
        } else if (node.nodeType === Node.ELEMENT_NODE && node.matches('section') && node.querySelector('h2')) {
          node.classList.add('tb-article-section');
          section = null;
        } else if (section) {
          section.append(node);
        }
      }
      content.querySelectorAll('table').forEach(table => {
        if (table.closest('.tb-article-table-wrap')) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'tb-article-table-wrap';
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', table.caption?.textContent.trim() || 'Tableau de l’article');
        table.before(wrapper);
        wrapper.append(table);
      });
      const headings = [...content.querySelectorAll('h2')].filter(h => h.textContent.trim());
      const prefix = (this.dataset.sectionId || 'article').replace(/[^a-zA-Z0-9_-]/g, '-');
      const seen = new Set();
      headings.forEach((heading, index) => {
        let id = heading.id;
        if (!id || seen.has(id) || document.getElementById(id) !== heading) {
          id = `article-${prefix}-${index + 1}`;
          let suffix = 1;
          while (document.getElementById(id) && document.getElementById(id) !== heading) id = `article-${prefix}-${index + 1}-${suffix++}`;
          heading.id = id;
        }
        seen.add(id);
        if (!heading.hasAttribute('tabindex')) heading.tabIndex = -1;
      });
      const scope = this.closest('.section-article-template') || this.parentElement;
      const time = scope?.querySelector('[data-article-reading-time]');
      const words = content.textContent.trim().split(/\s+/).filter(Boolean).length;
      if (time && words) {
        time.textContent = `${Math.max(1, Math.ceil(words / 200))} min de lecture`;
        time.hidden = false;
      }
      const nav = this.querySelector('.tb-document-contents');
      const list = this.querySelector('[data-document-links]');
      const enabled = !!nav && !!list && headings.length >= 2;
      this.classList.toggle('has-contents', enabled);
      if (!nav || !list) return;
      list.replaceChildren();
      headings.forEach(heading => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.textContent = heading.textContent.trim();
        link.setAttribute('href', `#${encodeURIComponent(heading.id)}`);
        item.append(link);
        list.append(item);
      });
      nav.hidden = !enabled;
      if (!enabled) return;
      this._onClick = event => {
        const link = event.target.closest('a[href^="#"]');
        if (!link || !nav.contains(link) || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        let target;
        try { target = document.getElementById(decodeURIComponent(link.hash.slice(1))); } catch { return; }
        if (!target || !content.contains(target)) return;
        event.preventDefault();
        try { history.pushState(null, '', link.hash); } catch { location.hash = link.hash; }
        target.scrollIntoView({ behavior: 'auto', block: 'start' });
        target.focus({ preventScroll: true });
        this._setCurrent(target.id);
      };
      nav.addEventListener('click', this._onClick);
      this._nav = nav;
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(entries => {
          const visible = entries.filter(entry => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible[0]) this._setCurrent(visible[0].target.id);
        }, { rootMargin: '-18% 0px -65% 0px', threshold: 0 });
        headings.forEach(heading => this._observer.observe(heading));
      }
    }
    _setCurrent(id) {
      this.querySelectorAll('[data-document-links] a').forEach(link => {
        if (decodeURIComponent(link.hash.slice(1)) === id) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }
    disconnectedCallback() {
      this._observer?.disconnect();
      this._observer = null;
      if (this._nav && this._onClick) this._nav.removeEventListener('click', this._onClick);
      this._nav = null;
      this._onClick = null;
    }
  }
  customElements.define('tb-article-reader', ArticleReader);
})();
